/* ============================= */
/* PASTORAL INTELLIGENCE ENGINE  */
/* Operational risk detection    */
/* for Kéves pastoral system     */
/* ============================= */

const p = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/* ============================= */
/* VISIT THRESHOLDS — unified    */
/* Single source of truth for    */
/* all visit health logic        */
/* ============================= */

export const VISIT_THRESHOLDS = {
  healthy:   21,   // ≤ 21 days
  attention: 45,   // ≤ 45 days
  neglected: 90,   // ≤ 90 days
  // > 90 = critical
};

/* ============================= */
/* RISK LEVELS                   */
/* ============================= */

export const RISK_LEVEL = {
  critical: "critical",
  high:     "high",
  medium:   "medium",
  healthy:  "healthy",
};

export const RISK_SCORE_THRESHOLDS = {
  critical: 60,
  high:     30,
  medium:   10,
};

export const RISK_LEVEL_UI = {
  critical: { badgeClass: "bg-red-100 text-red-700",      dotClass: "bg-red-500",     label: "Crítico"   },
  high:     { badgeClass: "bg-orange-100 text-orange-700", dotClass: "bg-orange-500",  label: "Alto"      },
  medium:   { badgeClass: "bg-amber-100 text-amber-700",  dotClass: "bg-amber-500",   label: "Atención"  },
  healthy:  { badgeClass: "bg-emerald-100 text-emerald-700", dotClass: "bg-emerald-500", label: "Saludable" },
};

/* ============================= */
/* VISIT HEALTH UI               */
/* Replaces getVisitHealth()     */
/* and getVisitHealthColor()     */
/* across all pages              */
/* ============================= */

export const VISIT_HEALTH_UI = {
  healthy: {
    iconClass:  "text-emerald-500",
    valueClass: "text-emerald-600 dark:text-emerald-400",
    badgeClass: "bg-emerald-100 text-emerald-700",
    barClass:   "bg-emerald-500",
    barPercent: 100,
    label:      "Saludable",
  },
  attention: {
    iconClass:  "text-yellow-500",
    valueClass: "text-yellow-600 dark:text-yellow-400",
    badgeClass: "bg-yellow-100 text-yellow-700",
    barClass:   "bg-yellow-400",
    barPercent: 70,
    label:      "Atención",
  },
  neglected: {
    iconClass:  "text-orange-500",
    valueClass: "text-orange-600 dark:text-orange-400",
    badgeClass: "bg-orange-100 text-orange-700",
    barClass:   "bg-orange-400",
    barPercent: 40,
    label:      "Descuidado",
  },
  critical: {
    iconClass:  "text-red-500",
    valueClass: "text-red-600 dark:text-red-400",
    badgeClass: "bg-red-100 text-red-700",
    barClass:   "bg-red-500",
    barPercent: 15,
    label:      "Urgente",
  },
  unknown: {
    iconClass:  "text-slate-400",
    valueClass: "text-slate-400",
    badgeClass: "bg-slate-100 text-slate-500",
    barClass:   "bg-slate-300",
    barPercent: 0,
    label:      "Sin visitas",
  },
};

export function getVisitHealthUI(days) {
  if (days === null || days === undefined) return VISIT_HEALTH_UI.unknown;
  if (days <= VISIT_THRESHOLDS.healthy)    return VISIT_HEALTH_UI.healthy;
  if (days <= VISIT_THRESHOLDS.attention)  return VISIT_HEALTH_UI.attention;
  if (days <= VISIT_THRESHOLDS.neglected)  return VISIT_HEALTH_UI.neglected;
  return VISIT_HEALTH_UI.critical;
}

/* ============================= */
/* SIGNAL TYPES                  */
/* Exported for smart notifs     */
/* and future assistant          */
/* ============================= */

export const SIGNAL_TYPES = {
  NO_VISIT_CRITICAL:   "NO_VISIT_CRITICAL",
  NO_VISIT_HIGH:       "NO_VISIT_HIGH",
  NO_VISIT_MEDIUM:     "NO_VISIT_MEDIUM",
  URGENT_PRAYER:       "URGENT_PRAYER",
  ACTIVE_PRAYERS:      "ACTIVE_PRAYERS",
  BAPTISM_PENDING:     "BAPTISM_PENDING",
  CRITICAL_MEMBERS:    "CRITICAL_MEMBERS",
  LOW_SPIRITUAL_SCORE: "LOW_SPIRITUAL_SCORE",
  OVERDUE_ALLOCATIONS: "OVERDUE_ALLOCATIONS",
  PENDING_ALLOCATIONS: "PENDING_ALLOCATIONS",
  NO_RECENT_VISITS:    "NO_RECENT_VISITS",
  LOW_ACTIVITY:        "LOW_ACTIVITY",
};

/* ============================= */
/* SIGNAL FACTORIES              */
/* alertType: "red"|"amber"|null */
/* null = internal signal only   */
/* ============================= */

const SIGNALS = {
  NO_VISIT_CRITICAL:   (days) => ({ type: SIGNAL_TYPES.NO_VISIT_CRITICAL,   severity: "critical", weight: 40, alertType: "red",   message: `Sin visita hace ${days} días` }),
  NO_VISIT_HIGH:       (days) => ({ type: SIGNAL_TYPES.NO_VISIT_HIGH,       severity: "high",     weight: 20, alertType: "amber", message: `Sin visita hace ${days} días` }),
  NO_VISIT_MEDIUM:     (days) => ({ type: SIGNAL_TYPES.NO_VISIT_MEDIUM,     severity: "medium",   weight: 10, alertType: null,    message: `Sin visita hace ${days} días` }),
  URGENT_PRAYER:       (n)    => ({ type: SIGNAL_TYPES.URGENT_PRAYER,       severity: "critical", weight: 35, alertType: "red",   message: p(n, "oración urgente", "oraciones urgentes") }),
  ACTIVE_PRAYERS:      (n)    => ({ type: SIGNAL_TYPES.ACTIVE_PRAYERS,      severity: "low",      weight: 3,  alertType: null,    message: p(n, "oración activa", "oraciones activas") }),
  BAPTISM_PENDING:     ()     => ({ type: SIGNAL_TYPES.BAPTISM_PENDING,     severity: "medium",   weight: 8,  alertType: null,    message: "Bautismo en proceso" }),
  CRITICAL_MEMBERS:    (n)    => ({ type: SIGNAL_TYPES.CRITICAL_MEMBERS,    severity: "critical", weight: 35, alertType: "amber", message: p(n, "miembro en estado crítico", "miembros en estado crítico") }),
  LOW_SPIRITUAL_SCORE: ()     => ({ type: SIGNAL_TYPES.LOW_SPIRITUAL_SCORE, severity: "high",     weight: 25, alertType: null,    message: "Estado espiritual bajo" }),
  OVERDUE_ALLOCATIONS: (n)    => ({ type: SIGNAL_TYPES.OVERDUE_ALLOCATIONS, severity: "critical", weight: 40, alertType: "red",   message: `${p(n, "asignación vencida", "asignaciones vencidas")} (más de 30 días)` }),
  PENDING_ALLOCATIONS: (n)    => ({ type: SIGNAL_TYPES.PENDING_ALLOCATIONS, severity: "medium",   weight: 15, alertType: "amber", message: p(n, "asignación de materiales pendiente", "asignaciones de materiales pendientes") }),
  NO_RECENT_VISITS:    (days) => ({ type: SIGNAL_TYPES.NO_RECENT_VISITS,    severity: "high",     weight: 30, alertType: "amber", message: `Sin registro de visita hace ${days} días` }),
  LOW_ACTIVITY:        ()     => ({ type: SIGNAL_TYPES.LOW_ACTIVITY,        severity: "medium",   weight: 20, alertType: null,    message: "Actividad ministerial baja" }),
};

/* ============================= */
/* INTERNALS                     */
/* ============================= */

function computeScore(signals) {
  return Math.min(signals.reduce((sum, s) => sum + s.weight, 0), 100);
}

function scoreToLevel(score) {
  if (score >= RISK_SCORE_THRESHOLDS.critical) return RISK_LEVEL.critical;
  if (score >= RISK_SCORE_THRESHOLDS.high)     return RISK_LEVEL.high;
  if (score >= RISK_SCORE_THRESHOLDS.medium)   return RISK_LEVEL.medium;
  return RISK_LEVEL.healthy;
}

function signalsToAlerts(signals) {
  return signals
    .filter((s) => s.alertType !== null && s.alertType !== undefined)
    .map((s) => ({ type: s.alertType, message: s.message }));
}

/* ============================= */
/* PRIORITY ENGINE               */
/* ============================= */

const PRIORITY_LEVEL_ORDER = { critical: 0, high: 1, medium: 2, healthy: 3 };

export function buildOperationalPriorities(signals) {
  const priorities = [];
  const seen = new Set();

  const add = (key, message, level) => {
    if (!seen.has(key)) {
      seen.add(key);
      priorities.push({ key, message, level });
    }
  };

  for (const s of signals) {
    switch (s.type) {
      case SIGNAL_TYPES.NO_VISIT_CRITICAL:
        add("visit",            "Visitar esta semana",                   "critical"); break;
      case SIGNAL_TYPES.NO_VISIT_HIGH:
        add("visit",            "Visitar en los próximos días",          "high");     break;
      case SIGNAL_TYPES.URGENT_PRAYER:
        add("prayer",           "Atender la oración urgente",            "critical"); break;
      case SIGNAL_TYPES.BAPTISM_PENDING:
        add("baptism",          "Acompañar el proceso bautismal",        "medium");   break;
      case SIGNAL_TYPES.CRITICAL_MEMBERS:
        add("members",          "Visitar miembros en estado crítico",    "critical"); break;
      case SIGNAL_TYPES.LOW_SPIRITUAL_SCORE:
        add("spiritual",        "Retomar el acompañamiento espiritual",  "high");     break;
      case SIGNAL_TYPES.OVERDUE_ALLOCATIONS:
        add("materials",        "Atender las asignaciones vencidas",     "critical"); break;
      case SIGNAL_TYPES.PENDING_ALLOCATIONS:
        add("materials-pending","Revisar las asignaciones pendientes",   "medium");   break;
      case SIGNAL_TYPES.NO_RECENT_VISITS:
        add("church-visit",     "Retomar el seguimiento pastoral",       "high");     break;
      case SIGNAL_TYPES.LOW_ACTIVITY:
        add("activity",         "Revisar la actividad de la iglesia",    "medium");   break;
      default:
        break;
    }
  }

  priorities.sort(
    (a, b) => (PRIORITY_LEVEL_ORDER[a.level] ?? 4) - (PRIORITY_LEVEL_ORDER[b.level] ?? 4)
  );

  return priorities;
}

/* ============================= */
/* PERSON RISK EVALUATOR         */
/* ============================= */

export function evaluatePersonRisk({
  days           = null,
  urgentPrayers  = 0,
  activePrayers  = 0,
  baptismStatus  = null,
} = {}) {
  const signals = [];

  if (days !== null) {
    if (days > VISIT_THRESHOLDS.neglected)  signals.push(SIGNALS.NO_VISIT_CRITICAL(days));
    else if (days > 60)                     signals.push(SIGNALS.NO_VISIT_HIGH(days));
    else if (days > VISIT_THRESHOLDS.attention) signals.push(SIGNALS.NO_VISIT_MEDIUM(days));
  }

  if (urgentPrayers > 0)       signals.push(SIGNALS.URGENT_PRAYER(urgentPrayers));
  else if (activePrayers > 0)  signals.push(SIGNALS.ACTIVE_PRAYERS(activePrayers));

  if (baptismStatus === "estudiando") signals.push(SIGNALS.BAPTISM_PENDING());

  const score = computeScore(signals);
  const level = scoreToLevel(score);

  return {
    level,
    score,
    signals,
    alerts:     signalsToAlerts(signals),
    priorities: buildOperationalPriorities(signals),
  };
}

/* ============================= */
/* FAMILY RISK EVALUATOR         */
/* ============================= */

export function evaluateFamilyRisk({
  days                = null,
  urgentPrayers       = 0,
  activePrayers       = 0,
  criticalMembersCount = 0,
  spiritualScore      = null,
} = {}) {
  const signals = [];

  if (days !== null) {
    if (days > VISIT_THRESHOLDS.neglected)  signals.push(SIGNALS.NO_VISIT_CRITICAL(days));
    else if (days > 60)                     signals.push(SIGNALS.NO_VISIT_HIGH(days));
    else if (days > VISIT_THRESHOLDS.attention) signals.push(SIGNALS.NO_VISIT_MEDIUM(days));
  }

  if (criticalMembersCount > 0) signals.push(SIGNALS.CRITICAL_MEMBERS(criticalMembersCount));

  if (urgentPrayers > 0)       signals.push(SIGNALS.URGENT_PRAYER(urgentPrayers));
  else if (activePrayers > 0)  signals.push(SIGNALS.ACTIVE_PRAYERS(activePrayers));

  if (spiritualScore !== null && spiritualScore <= 2) signals.push(SIGNALS.LOW_SPIRITUAL_SCORE());

  const score = computeScore(signals);
  const level = scoreToLevel(score);

  return {
    level,
    score,
    signals,
    alerts:     signalsToAlerts(signals),
    priorities: buildOperationalPriorities(signals),
  };
}

/* ============================= */
/* CHURCH RISK EVALUATOR         */
/* ============================= */

export function evaluateChurchRisk({
  lastVisitDays      = null,
  overdueAllocsCount = 0,
  pendingAllocsCount = 0,
  eventsCount        = 0,
  familiesCount      = 0,
  visitsCount        = 0,
} = {}) {
  const signals = [];

  if (overdueAllocsCount > 0) signals.push(SIGNALS.OVERDUE_ALLOCATIONS(overdueAllocsCount));
  if (pendingAllocsCount > 0) signals.push(SIGNALS.PENDING_ALLOCATIONS(pendingAllocsCount));

  if (lastVisitDays !== null && lastVisitDays > VISIT_THRESHOLDS.neglected) {
    signals.push(SIGNALS.NO_RECENT_VISITS(lastVisitDays));
  }

  if (familiesCount > 0 && eventsCount === 0 && visitsCount === 0) {
    signals.push(SIGNALS.LOW_ACTIVITY());
  }

  const score = computeScore(signals);
  const level = scoreToLevel(score);

  return {
    level,
    score,
    signals,
    alerts:     signalsToAlerts(signals),
    priorities: buildOperationalPriorities(signals),
  };
}
